import { m as motion } from "framer-motion";
import { ArrowRight, Check, Star, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { fadeInUp, defaultViewport, defaultTransition, fastTransition } from "@/lib/motion";
import { EXTERNAL_LINKS, MEMBERSHIP_LINKS } from "@/lib/constants";
import {
  EARLY_BIRD_DISCOUNT_CODE,
  EARLY_BIRD_DISCOUNT_LABEL,
} from "@/lib/cohorts";

interface Program {
  name: string;
  price: string;
  priceSuffix?: string;
  description: string;
  link: string;
  tag: string;
  features: string[];
  highlighted?: boolean;
  buttonText: string;
}

const programs: Program[] = [
  {
    name: "Free Membership",
    price: "Free",
    description: "Join the community and start exploring AI for HR.",
    link: MEMBERSHIP_LINKS.PORTAL,
    tag: "Get Started",
    buttonText: "Join Free",
    features: [
      "Monthly live workshops",
      "Free implementation guides",
      "Community access",
    ],
  },
  {
    name: "Standard Membership",
    price: "$30",
    priceSuffix: "/mo",
    description: "Self-paced course with full tools and community.",
    link: MEMBERSHIP_LINKS.STANDARD,
    tag: "Self-Paced",
    buttonText: "Join Now",
    features: [
      "Core AI for HR course",
      "18+ implementation guides",
      "AI Chat Platform (6 frontier models)",
      "Custom AI agents (JD Pro & more)",
      "Workshop recording library",
      "Certificate of completion",
    ],
  },
  {
    name: "Premium Membership",
    price: "$1,400",
    priceSuffix: " one-time",
    description: "Lifetime access with live cohorts, coaching, and VIP community.",
    link: EXTERNAL_LINKS.PREMIUM_MASTERMIND_APPLICATION,
    tag: "Most Popular",
    highlighted: true,
    buttonText: "Apply Now",
    features: [
      "Everything in Standard",
      `${EARLY_BIRD_DISCOUNT_LABEL} with code ${EARLY_BIRD_DISCOUNT_CODE}`,
      "Lifetime access to all content",
      "2 live cohort intensives per year",
      "Monthly tech clinics (group coaching)",
      "AI Readiness assessment",
      "VIP community access",
      "Quarterly guest expert sessions",
      "Limited to 200 members",
    ],
  },
  {
    name: "Private Team Training",
    price: "Custom",
    description: "Customized private cohort for your HR organization.",
    link: "/team-training",
    tag: "Enterprise",
    buttonText: "Learn More",
    features: [
      "Custom curriculum for your org",
      "Dedicated facilitators",
      "Team progress tracking",
      "Flexible scheduling",
    ],
  },
];

export const ProgramPathwaySection = () => {
  return (
    <motion.section
      initial={fadeInUp.initial}
      whileInView={fadeInUp.animate}
      viewport={defaultViewport}
      transition={defaultTransition}
      className="container-class bg-foreground"
    >
      <div className="section-inner">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-background">
            <span className="gradient-text">Ways to Learn and Work With Us</span>
          </h2>
          <p className="text-lg text-background/70 max-w-3xl mx-auto mt-4">
            From exploring on your own to training your whole team — there's a way in at every stage.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-7xl mx-auto items-start">
          {programs.map((p, i) => {
            const isHighlighted = p.highlighted;
            const cardClassName = `group flex flex-col h-full rounded-xl p-5 transition-all duration-300 ${
              isHighlighted
                ? 'bg-card border-2 border-primary/40 shadow-xl shadow-primary/10 hover:shadow-2xl hover:shadow-primary/20 hover:-translate-y-1 pt-8'
                : 'card-light hover-lift'
            }`;

            const cardContent = (
              <>
                {/* Tag (non-highlighted only) */}
                {!isHighlighted && (
                  <span className="inline-block text-xs font-semibold uppercase tracking-wider text-primary bg-primary/10 px-2.5 py-1 rounded-full self-start mb-3">
                    {p.tag}
                  </span>
                )}

                {/* Name & Description */}
                <h3 className={`text-lg font-bold mb-1 ${isHighlighted ? 'text-foreground' : ''}`}>
                  {p.name}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {p.description}
                </p>

                {/* Price */}
                <div className="mb-4">
                  <span className="text-2xl font-bold text-primary">
                    {p.price}
                  </span>
                  {p.priceSuffix && (
                    <span className="text-sm text-muted-foreground">{p.priceSuffix}</span>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-2 flex-grow mb-5">
                  {p.features.map((feature, fi) => (
                    <li key={fi} className="flex items-start gap-2">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isHighlighted ? 'text-primary' : 'text-secondary'}`} />
                      <span className="text-sm text-foreground/80 leading-snug">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className={`mt-auto pt-4 border-t ${isHighlighted ? 'border-primary/20' : 'border-border/30'}`}>
                  <span className={`inline-flex items-center gap-2 text-sm font-semibold transition-all ${
                    isHighlighted
                      ? 'text-primary group-hover:gap-3'
                      : 'text-muted-foreground group-hover:text-primary group-hover:gap-3'
                  }`}>
                    {p.buttonText}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </>
            );

            return (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={defaultViewport}
                transition={{ ...fastTransition, delay: i * 0.08 }}
                className={`relative flex flex-col h-full ${isHighlighted ? 'lg:-mt-4 lg:mb-0' : ''}`}
              >
                {/* Most Popular badge */}
                {isHighlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-primary text-background text-xs font-bold uppercase tracking-wider shadow-lg">
                      <Star className="w-3.5 h-3.5 fill-current" />
                      {p.tag}
                    </div>
                  </div>
                )}

                {p.link.startsWith("/") ? (
                  <Link to={p.link} className={cardClassName}>
                    {cardContent}
                  </Link>
                ) : (
                  <a href={p.link} target="_blank" rel="noopener noreferrer" className={cardClassName}>
                    {cardContent}
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Recruiting Masterclass callout */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={defaultViewport}
          transition={{ ...fastTransition, delay: 0.4 }}
          className="mt-8 max-w-2xl mx-auto text-center"
        >
          <a
            href="https://www.workplacelabs.io/courses"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 text-sm text-background/60 hover:text-background transition-colors"
          >
            <Sparkles className="w-4 h-4 text-primary" />
            Also available: <span className="font-semibold underline underline-offset-4">Recruiting Masterclass</span> — self-paced for $199
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </motion.section>
  );
};
